'use client';

import React from 'react';
import { Search, X } from 'lucide-react';
import type { AuditFilters } from '@/lib/services/staffingService';

interface AuditFiltersProps {
  filters: AuditFilters;
  onChange: (filters: AuditFilters) => void;
}

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'role_change', label: 'Role Change' },
  { value: 'credential_update', label: 'Credential Update' },
  { value: 'shift_modification', label: 'Shift Modification' },
  { value: 'approval', label: 'Approval' },
  { value: 'timesheet_approved', label: 'Timesheet Approved' },
  { value: 'timesheet_rejected', label: 'Timesheet Rejected' },
  { value: 'user_created', label: 'User Created' },
  { value: 'user_deactivated', label: 'User Deactivated' },
  { value: 'invoice_created', label: 'Invoice Created' },
  { value: 'invoice_updated', label: 'Invoice Updated' },
  { value: 'job_posted', label: 'Job Posted' },
  { value: 'applicant_stage_changed', label: 'Applicant Stage Changed' },
  { value: 'settings_updated', label: 'Settings Updated' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
];

const SEVERITIES = [
  { value: '', label: 'All Severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

export default function AuditFilters({ filters, onChange }: AuditFiltersProps) {
  const hasActiveFilters =
    filters.search || filters.actionType || filters.severity || filters.dateFrom || filters.dateTo;

  const handleClear = () => {
    onChange({});
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by user, description, target…"
            value={filters.search || ''}
            onChange={(e) => onChange({ ...filters, search: e.target.value || undefined })}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Action Type */}
        <select
          value={filters.actionType || ''}
          onChange={(e) => onChange({ ...filters, actionType: (e.target.value as AuditFilters['actionType']) || undefined })}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
        >
          {ACTION_TYPES.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>

        {/* Severity */}
        <select
          value={filters.severity || ''}
          onChange={(e) => onChange({ ...filters, severity: (e.target.value as AuditFilters['severity']) || undefined })}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
        >
          {SEVERITIES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        {/* Date From */}
        <input
          type="date"
          value={filters.dateFrom || ''}
          onChange={(e) => onChange({ ...filters, dateFrom: e.target.value || undefined })}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
        />

        {/* Date To */}
        <input
          type="date"
          value={filters.dateTo || ''}
          onChange={(e) => onChange({ ...filters, dateTo: e.target.value || undefined })}
          className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
        />

        {/* Clear */}
        {hasActiveFilters && (
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
